/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// wire-tools.js — everything about jumper wires the user drives: the
// click-click wire TOOL (anchor a free point, click a second to lay a wire;
// the colour STAYS put between wires — change it via the toolbar swatch),
// grabbing a direct wire's BODY to translate it rigidly, BENDING a routed one
// (an Option/Alt press), dragging one END cap to re-route it (an Option/Alt press: in the
// desk's pin-targeting mode a wire end is a lead like a resistor's leg; see
// CLAUDE.md → "Selection"), and the per-wire context menu —
// the SAME uniform shape every
// part/board menu has (Pin Assignment / Properties… / Delete Component; see
// desk-controller.js's #onPartContextMenu), with Pin Assignment and
// Properties… opening their dialogs through host callbacks so this file stays
// wire-behaviour-only. Pulled out of DeskController so "wiring" is one module.
//
// BENDING a routed wire is an Option/Alt press, from the desk's pin-targeting
// mode (bendTargetAt): pressing anywhere along the run that is not a lead and
// dragging INSERTS a waypoint at that point (pressing an existing waypoint
// moves it instead), and letting either go on top of a neighbouring point —
// another waypoint, or one of the wire's own ends — MERGES it away, which is
// how a bend is taken back out. A PLAIN press on a routed wire is not this
// file's: the controller moves the wire whole, ends and bends together, as a
// one-wire group drag (Jason, 2026-10-20).
// A wire only ever has MAX_WIRE_POINTS bends; the cap says so rather than
// silently ignoring the drag.
//
// It reuses the controller's shared `#mode` (through the host) so the viewport
// pointer dispatcher's mode checks stay exactly as they were — this is a home
// for the wire behaviour, not a change to how modes arbitrate. Geometry comes
// from the pure model/part-geometry.js helpers; the host supplies the shared
// surface (doc, deskView, wire layer, hover ring) and coordination hooks.

import { t } from "../i18n.js";
import { PopupManager } from "../popup-manager.js";
import { PX_PER_UNIT } from "../desk/desk-geometry.js";
import { MAX_WIRE_POINTS, WIRE_COLORS } from "../model/desk-doc.js";
import {
  WIRE_POINT_MERGE_RADIUS,
  addressWorld,
  connectionPointAt,
  nearestLegalPoint,
  wireEndNear,
  wirePointNear,
} from "../model/part-geometry.js";
import { nearestOnPolyline } from "../desk/wire-path.js";
import { HANDLE_HIT_RADIUS, busEndHandleNear } from "../desk/ribbon-path.js";
import { nearestLegalOffset } from "../model/nearest-legal.js";
import { beginPointerGesture, releaseWorld } from "./pointer-gesture.js";
import { aimRing } from "./hole-rings.js";

/** Pointer travel (px) below which a press stays a click, not a drag. */
const DRAG_THRESHOLD = 4;
/** How close (pitch units) an Option/Alt press must come to a routed wire's
    run to lay a bend in it — a shade past half the 10 px `.wire-hit` stroke a
    plain press on the body answers to (0.5), so anywhere the wire takes a
    plain press it takes an Option one too. */
const WIRE_RUN_GRAB_RADIUS = 0.6;
/** How far (pitch units) a WHOLE-WIRE (rigid, both-ends-together) drop may be
    nudged to land legally. It stays small on purpose: a big rigid jump reads
    as relocating the whole wire, not recovering a drop that missed by a hole
    or two. Its offsets are whole-pitch steps from the wire's own HOLES, i.e.
    from points ON the lattice — see part-geometry.js's END_SNAP_RADIUS for
    why a single end, which starts from the cursor, cannot search that way. */
const SNAP_RADIUS = 2;

export class WireTools {
  #host;
  #colorIndex = 0; // the color the next committed wire gets (pinned; never cycles)

  /**
   * @param {object} host - shared controller surface:
   *   { get mode, set mode, doc, deskView, viewport, wireLayer, ring,
   *     emitDocChanged, hideHover, selectWire, deselect, cancelPlacement,
   *     disarmProbe, get probeArmed, get editingLocked, clearSelectionIfWire,
   *     onStateChange, onOpenPinout, onOpenProperties }
   */
  constructor(host) {
    this.#host = host;
  }

  // ── Wire tool (click-click) ───────────────────────────────────────────────

  get armed() {
    return this.#host.mode?.kind === "wire";
  }

  /** Armed with a wire's first end already laid (the rubber band out). */
  get pending() {
    return this.armed && this.#host.mode.from != null;
  }

  /** The color the next committed wire gets. */
  get color() {
    return WIRE_COLORS[this.#colorIndex];
  }

  /** Pin the next wire color (the toolbar swatch strip). */
  setColor(color) {
    const i = WIRE_COLORS.indexOf(color);
    if (i === -1) {
      const err = new Error(`unknown wire color: ${color}`);
      err.code = "INVALID_ARG";
      throw err;
    }
    this.#colorIndex = i;
    this.#notifyState();
  }

  /** Arm click-click wiring; a second call to toggle goes through app.js. */
  arm() {
    if (this.armed || this.#host.editingLocked) return;
    this.#host.cancelPlacement();
    this.#host.disarmProbe();
    this.#host.disarmBusTool?.();
    this.#host.disarmCutTool?.();
    this.#host.deselect();
    this.#host.hideHover();
    this.#host.mode = { kind: "wire", from: null, hover: null };
    this.#host.viewport.classList.add("desk-viewport--wiring");
    this.#notifyState();
  }

  disarm() {
    if (!this.armed) return;
    this.#clearPending();
    this.#host.mode = null;
    this.#host.viewport.classList.remove("desk-viewport--wiring");
    this.#host.ring.hidden = true;
    this.#host.ring.classList.remove("hole-ring--illegal");
    this.#notifyState();
  }

  toggle() {
    if (this.armed) this.disarm();
    else this.arm();
  }

  /** Escape while wiring: first cancel a pending wire, then disarm. */
  handleEscape() {
    if (!this.armed) return false;
    if (this.#host.mode.from) this.#clearPending();
    else this.disarm();
    return true;
  }

  /** Cancel a pending (anchored-but-uncommitted) wire without disarming. */
  cancelPending() {
    this.#clearPending();
  }

  #clearPending() {
    const m = this.#host.mode;
    if (m?.kind !== "wire") return;
    m.from = null;
    this.#host.wireLayer.setPreview(null);
  }

  #notifyState() {
    this.#host.onStateChange?.({ armed: this.armed, color: this.color });
  }

  /**
   * Must this release be thrown away rather than committed? Two reasons, and
   * the second is the one that is easy to miss: a real `pointercancel` (or the
   * synthetic one `cancelDrag` routes through), OR a drag that SPANNED Run.
   *
   * Space and ⌘R reach the transport while a drag is in flight — app.js only
   * declines them for an armed TOOL, and a drag is not one — so the circuit can
   * start under a gesture that began while editing was still allowed. Every
   * DeskController drag has always treated that as a cancel; these did not, and
   * quietly committed a topology edit into a running circuit. `disarm()` cannot
   * catch it either: `armed` is false once a drag owns `#mode`.
   */
  #aborted(e) {
    return e.type === "pointercancel" || this.#host.editingLocked;
  }

  #wirePointAt(world) {
    const doc = this.#host.doc;
    return connectionPointAt(doc.boards, doc.components, world);
  }

  #addressWorld(address) {
    const doc = this.#host.doc;
    return addressWorld(doc.boards, doc.components, address);
  }

  /** Wire-mode pointermove: instant ring with legality + the rubber band. */
  trackMove(e) {
    const m = this.#host.mode;
    const world = this.#host.deskView.worldFromEvent(e);
    const hit = this.#wirePointAt(world);
    if (hit) {
      const free = this.#host.doc.isHoleFree(hit.address);
      const legal = free && hit.address !== m.from;
      m.hover = { address: hit.address, legal };
      aimRing(this.#host.ring, hit, legal);
    } else {
      m.hover = null;
      aimRing(this.#host.ring, null);
    }
    if (m.from) {
      const from = this.#addressWorld(m.from);
      if (!from) {
        // The anchored hole vanished from under us (e.g. an undo/redo removed
        // its board while the wire tool stayed armed) — drop the stale anchor
        // instead of crashing on a null world point.
        this.#clearPending();
      } else {
        this.#host.wireLayer.setPreview({
          from: { x: from.x * PX_PER_UNIT, y: from.y * PX_PER_UNIT },
          to: hit
            ? { x: hit.x * PX_PER_UNIT, y: hit.y * PX_PER_UNIT }
            : { x: world.x * PX_PER_UNIT, y: world.y * PX_PER_UNIT },
          color: this.color,
          // Danger tint only over an actual occupied/self point — over empty
          // desk the band stays its color (a click there just does nothing).
          legal: hit ? m.hover.legal : true,
        });
      }
    }
  }

  /** Wire-mode click: anchor on the first free point, commit on the second. */
  commitClick(e) {
    const m = this.#host.mode;
    this.trackMove(e); // legality/hover at the exact click point
    if (!m.hover?.legal) return;
    // An anchor something else has since taken (an undo put a wire back into
    // that hole while the tool stayed armed) is dropped, and this click
    // anchors afresh — committing would throw out of the click handler.
    if (m.from && !this.#host.doc.isHoleFree(m.from)) this.#clearPending();
    if (!m.from) {
      m.from = m.hover.address;
      return;
    }
    this.#host.doc.addWire({
      from: m.from,
      to: m.hover.address,
      color: this.color,
      // The app-wide default (Settings ▸ Appearance ▸ "Wire layout"), read at
      // placement time exactly as the default LED colour is — it seeds a NEW
      // wire and nothing more; an existing one is changed through its own
      // Properties dialog.
      layout: this.#host.defaultWireLayout ?? "direct",
    });
    // The colour STAYS put between wires — a chain of jumpers keeps the colour
    // you picked; change it deliberately via the toolbar swatch.
    this.#clearPending(); // re-arm fresh — chain-friendly
    this.#host.emitDocChanged("add wire");
    this.#notifyState();
  }

  /** Remove a wire; clears its selection. */
  removeWire(id) {
    this.#host.doc.removeWire(id);
    this.#host.clearSelectionIfWire(id);
    this.#host.emitDocChanged("delete wire");
  }

  /** Recolor a wire (context menu). */
  recolorWire(id, color) {
    this.#host.doc.recolorWire(id, color);
    this.#host.emitDocChanged("recolor wire");
  }

  /** The SAME three items every part/board menu has, always, in the same
      order — Pin Assignment / Properties… / Delete Component. A wire always
      has both (2 endpoints; Name/Description + Color), so neither is ever
      disabled — see desk-controller.js's #onPartContextMenu for the sibling
      convention on parts. */
  onContextMenu(id, e) {
    e.preventDefault();
    // While probing, a wire right-click belongs to the PROBE: it names the net
    // the wire sits on. Bail and let the event bubble to the viewport handler
    // (the same rule boards/parts/annotations follow) — otherwise BOTH menus
    // open, and since PopupManager QUEUES the second rather than replacing it,
    // the wire menu is what the user sees first, with the net menu behind it.
    if (this.#host.probeArmed) return;
    if (this.#host.editingLocked) this.#host.refuseEdit?.();
    if (this.#host.mode || this.#host.editingLocked) return; // frozen while running
    this.#host.selectWire(id);
    PopupManager.menu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: t("desk.menu.pinAssignment"),
          onSelect: () => this.#host.onOpenPinout(id),
        },
        { separator: true },
        {
          label: t("desk.menu.properties"),
          onSelect: () => this.#host.onOpenProperties(id),
        },
        { separator: true },
        {
          label: t("desk.menu.deleteComponent"),
          danger: true,
          onSelect: () => this.removeWire(id),
        },
      ],
    });
  }

  // ── Grabbing a placed wire (whole-wire translate / bend / one end) ────────

  /**
   * A plain press on a DIRECT wire's BODY (the controller has already decided
   * it is this wire's — CLAUDE.md → "Selection" — and takes a routed one's
   * itself): select it, and start the drag that translates it rigidly. A bus
   * member's own lead has no rigid translate (its middle belongs to the
   * ribbon), so the press only selects it. Returns true when a drag started.
   */
  beginBodyDrag(wireId, e) {
    const doc = this.#host.doc;
    const wire = doc.getWire(wireId);
    if (!wire) return false;
    if (doc.busOfWire(wireId)) {
      this.#host.selectWire(wireId);
      return false;
    }
    this.#beginWholeDrag(wireId, e);
    return this.#host.mode?.kind === "drag-wire";
  }

  /**
   * An Option/Alt press on a wire END (pin-targeting mode — the controller has
   * already picked the end and made it the selection): drag that one end to a
   * new hole. `grab` is `{ wireId, end, origin }` (part-geometry's
   * wireEndNear). Selects nothing itself — the pick is the controller's.
   */
  beginEndDrag(grab, e) {
    if (!this.#host.doc.getWire(grab?.wireId)) return false;
    this.#beginEndpointDrag(grab, e);
    return true;
  }

  /**
   * May a press at `world` grab `wireId`'s cap there as a LEAD? Everywhere
   * except right where it would be confused with that bus's own end-handle:
   * the handle's collar sits at a fixed offset from the bus's CENTROID, not
   * from any particular member's hole, so almost every member's cap is nowhere
   * near it — but the one that happens to coincide (measured, not
   * hypothetical: an 8-bit bus parked a collar within a couple of screen px of
   * one member's own cap) would otherwise flip a coin between "grab the
   * handle" and "re-route this one wire" every time.
   */
  endGrabbable(wireId, world) {
    return !this.#nearOwnBusHandle(wireId, world);
  }

  /**
   * The bend an Option/Alt press at `world` would take (pin-targeting mode —
   * CLAUDE.md → "Selection"): a ROUTED wire's waypoint knob within
   * WIRE_POINT_GRAB_RADIUS, else the nearest point along a routed run within
   * WIRE_RUN_GRAB_RADIUS — where a new bend would go in. As
   * `{ wireId, index, insert, x, y }` (`index` the waypoint's own, or the
   * segment an insert lands in; `x`/`y` where the ring goes), or null. A bus
   * member is never routed, and a faded wire's run isn't drawn to aim at.
   */
  bendTargetAt(world) {
    if (this.#host.wireLayer.faded) return null;
    const doc = this.#host.doc;
    const routed = doc.wires.filter(
      (w) => w.layout === "routed" && !doc.busOfWire(w.id),
    );
    const knob = wirePointNear(routed, world);
    if (knob) {
      const { wireId, index, origin } = knob;
      return { wireId, index, insert: false, x: origin.x, y: origin.y };
    }
    let best = null;
    for (const wire of routed) {
      const poly = this.#routeWorld(wire);
      const hit = poly && nearestOnPolyline(poly, world);
      if (!hit || hit.distance > WIRE_RUN_GRAB_RADIUS) continue;
      if (!best || hit.distance < best.hit.distance) best = { wire, hit };
    }
    if (!best) return null;
    const { index, point } = best.hit;
    return { wireId: best.wire.id, index, insert: true, ...point };
  }

  /**
   * Start a bend drag on a routed wire — `target` as bendTargetAt names it: move the waypoint at `index`, or with `insert` lay a new
   * one in segment `index`. At the waypoint cap nothing is inserted — the
   * press still selects the wire, and the refusal is said out loud rather than
   * read as a dead drag. Returns true when a drag started.
   */
  beginBend(target, e) {
    const wire = this.#host.doc.getWire(target?.wireId);
    if (!wire) return false;
    if (target.insert && (wire.points?.length ?? 0) >= MAX_WIRE_POINTS) {
      this.#host.selectWire(wire.id);
      PopupManager.notify({
        title: t("wire.tooManyBends"),
        message: t("wire.tooManyBendsMessage", { max: MAX_WIRE_POINTS }),
      });
      return false;
    }
    this.#beginPointDrag(wire.id, target.index, target.insert, e);
    return true;
  }

  /**
   * A routed wire's full run in WORLD (pitch) units — its two resolved
   * endpoints with its waypoints between them, the same list WireLayer draws
   * (which works in px). Null when either end doesn't resolve.
   */
  #routeWorld(wire) {
    if (!wire) return null;
    const a = this.#addressWorld(wire.from);
    const b = this.#addressWorld(wire.to);
    if (!a || !b) return null;
    return [
      { x: a.x, y: a.y },
      ...(wire.points ?? []).map((p) => ({ ...p })),
      { x: b.x, y: b.y },
    ];
  }

  /**
   * Where a dragged point of `wire` would MERGE if released at `world`: the
   * index in its full run (endpoint 0, waypoint i+1, endpoint last) of the
   * nearest OTHER point within WIRE_POINT_MERGE_RADIUS, or null. `self` is the
   * run index the drag itself occupies, which can never be its own target.
   */
  #mergeTarget(poly, world, self) {
    let best = null;
    poly.forEach((p, i) => {
      if (i === self) return;
      const dist = Math.hypot(world.x - p.x, world.y - p.y);
      if (dist > WIRE_POINT_MERGE_RADIUS) return;
      if (!best || dist < best.dist) best = { index: i, point: p, dist };
    });
    return best;
  }

  /**
   * Start a waypoint drag: `index` is the waypoint's own index, and `insert`
   * says whether it exists yet (a press on the run inserts one at the segment
   * it landed on; a knob press moves the one it grabbed). Nothing is written to
   * the document until the release — an abandoned drag leaves no bend behind.
   */
  #beginPointDrag(wireId, index, insert, e) {
    this.#host.hideHover();
    this.#host.selectWire(wireId); // select on press (mode still null here)
    const mode = {
      kind: "drag-wire-point",
      wireId,
      index,
      insert,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      active: false,
      teardown: null,
    };
    this.#host.mode = mode;
    mode.teardown = beginPointerGesture(
      this.#host.wireLayer.element,
      e.pointerId,
      { onMove: this.#onPointMove, onEnd: this.#onPointUp },
    );
  }

  #onPointMove = (e) => {
    const m = this.#host.mode;
    if (m?.kind !== "drag-wire-point" || e.pointerId !== m.pointerId) return;
    if (!m.active) {
      const travel = Math.hypot(
        e.clientX - m.startClientX,
        e.clientY - m.startClientY,
      );
      if (travel < DRAG_THRESHOLD) return; // separate a click from a drag
      m.active = true;
      this.#host.viewport.classList.add("desk-viewport--wire-dragging");
    }
    const world = this.#host.deskView.worldFromEvent(e);
    m.lastWorld = world; // the fallback for a release with no position of its own
    this.#previewPoint(m, world);
  };

  /** What the point being dragged would merge into if released at `world`
      (null for none) — asked once per move, for the preview the release then
      commits as it stands. */
  #resolveMerge(m, world) {
    const poly = this.#routeWorld(this.#host.doc.getWire(m.wireId));
    if (!poly) return null;
    // The dragged point's own index in the full run: a moved waypoint already
    // sits there (+1 for the leading endpoint); an inserted one does not exist
    // in the run yet, so nothing is excluded from the merge search.
    return this.#mergeTarget(poly, world, m.insert ? -1 : m.index + 1);
  }

  /** Draw the in-flight bend. A merge target PULLS the point onto itself, so
      what the release does is what the preview showed — kept on the drag
      (`shown`) for the release to commit. */
  #previewPoint(m, world) {
    const merge = this.#resolveMerge(m, world);
    const at = merge ? merge.point : world;
    m.shown = { merge: Boolean(merge), at };
    this.#host.wireLayer.setPointDrag({
      wireId: m.wireId,
      index: m.index,
      insert: m.insert,
      world: { x: at.x * PX_PER_UNIT, y: at.y * PX_PER_UNIT },
      merge: Boolean(merge),
    });
  }

  #onPointUp = (e) => {
    const m = this.#host.mode;
    if (m?.kind !== "drag-wire-point" || e.pointerId !== m.pointerId) return;
    this.#host.mode = null;
    m.teardown?.();
    this.#host.viewport.classList.remove("desk-viewport--wire-dragging");
    this.#host.wireLayer.setPointDrag(null); // stop overriding; redraw from doc

    if (!m.active) return; // plain click — the wire is already selected
    if (this.#aborted(e)) return; // aborted — never commit

    // The drop is what the last move SHOWED, never re-resolved at the release
    // point (the single-point rule, CLAUDE.md → "Document model"): read
    // there, a release a hair past the merge reach kept a bend the knob had
    // shown merged, and one a hair inside deleted a bend shown kept (the
    // regrade, 2026-10-23).
    const shown = m.shown;
    if (!shown) return;
    const world = shown.at;
    const doc = this.#host.doc;
    if (!doc.getWire(m.wireId)) return; // it went away under us (an undo)
    if (shown.merge) {
      // Dropped onto a neighbour: an existing bend is merged away, and one
      // that was never added simply isn't.
      if (m.insert) return;
      doc.removeWirePoint(m.wireId, m.index);
      this.#host.emitDocChanged("merge wire point");
      return;
    }
    if (m.insert) doc.addWirePoint(m.wireId, m.index, world);
    else doc.moveWirePoint(m.wireId, m.index, world);
    this.#host.emitDocChanged(m.insert ? "bend wire" : "move wire point");
  };

  /**
   * Is `world` within `wireId`'s own bus's end-handle hit radius (false for
   * a non-member)? Reuses the SAME collar geometry WireLayer renders the
   * handles at (desk/ribbon-path.js), derived fresh from the bus's currently
   * resolved member endpoints rather than threading collar points through.
   */
  #nearOwnBusHandle(wireId, world) {
    // Faded wires put the whole ribbon away — band, handles and all (see
    // WireLayer's header) — so there is no handle left to lose the grab to,
    // and a member's cap re-routes like every other wire's.
    if (this.#host.wireLayer.faded) return false;
    const doc = this.#host.doc;
    const bus = doc.busOfWire(wireId);
    if (!bus) return false;
    const froms = [];
    const tos = [];
    for (const id of bus.members) {
      const wire = doc.getWire(id);
      const a = wire && this.#addressWorld(wire.from);
      const b = wire && this.#addressWorld(wire.to);
      if (a) froms.push({ x: a.x * PX_PER_UNIT, y: a.y * PX_PER_UNIT });
      if (b) tos.push({ x: b.x * PX_PER_UNIT, y: b.y * PX_PER_UNIT });
    }
    const point = { x: world.x * PX_PER_UNIT, y: world.y * PX_PER_UNIT };
    return Boolean(busEndHandleNear(froms, tos, point, HANDLE_HIT_RADIUS));
  }

  /**
   * Abort an in-flight endpoint or whole-wire drag (DeskController#
   * cancelDragGesture — Escape, or recovering a pointerup the browser
   * silently dropped). Routes a synthetic `pointercancel` through the same
   * up-handler a real one would reach, so it tears down the capture and
   * listeners and reverts without duplicating that logic here. A no-op when
   * neither drag kind is current.
   */
  cancelDrag() {
    const m = this.#host.mode;
    const fake = { type: "pointercancel", pointerId: m?.pointerId };
    if (m?.kind === "drag-wire-end") this.#onEndpointUp(fake);
    else if (m?.kind === "drag-wire") this.#onWholeUp(fake);
    else if (m?.kind === "drag-wire-point") this.#onPointUp(fake);
  }

  /** The wire whose end cap `world` is on/near, or null. A cap sits ON a
      board hole and is not a pointer target, so a press there lands on the
      board beneath — the controller asks this so the press is the WIRE's
      (body wins over the board under it), plain or ⌘/Ctrl. */
  wireIdNear(world) {
    const doc = this.#host.doc;
    return (
      wireEndNear(doc.boards, doc.components, doc.wires, world)?.wireId ?? null
    );
  }

  #beginEndpointDrag(grab, e) {
    this.#host.hideHover();
    const mode = {
      kind: "drag-wire-end",
      wireId: grab.wireId,
      end: grab.end,
      origin: grab.origin, // revert target
      shown: null, // the ringed target the release commits (#onEndpointMove)
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      active: false,
      teardown: null,
    };
    this.#host.mode = mode;
    // Capture on the persistent wire SVG — render() clears its CHILDREN but
    // keeps the element, so the capture survives live re-renders. The release
    // does NOT ride on that capture holding, though — see pointer-gesture.js.
    mode.teardown = beginPointerGesture(
      this.#host.wireLayer.element,
      e.pointerId,
      { onMove: this.#onEndpointMove, onEnd: this.#onEndpointUp },
    );
  }

  #onEndpointMove = (e) => {
    const m = this.#host.mode;
    if (m?.kind !== "drag-wire-end" || e.pointerId !== m.pointerId) return;
    if (!m.active) {
      const travel = Math.hypot(
        e.clientX - m.startClientX,
        e.clientY - m.startClientY,
      );
      if (travel < DRAG_THRESHOLD) return; // separate a click from a drag
      m.active = true;
      this.#host.viewport.classList.add("desk-viewport--wire-dragging");
    }
    const world = this.#host.deskView.worldFromEvent(e);
    const resolved = this.#resolveEndpointTarget(m.wireId, m.end, world);
    aimRing(this.#host.ring, resolved, resolved?.legal);
    // What the screen now SHOWS — snapped and ringed — and so exactly what the
    // release commits (#onEndpointUp never re-resolves). Null when this sample
    // has nowhere legal to land (no ring, or a red one), so wandering off a
    // target before letting go reverts.
    m.shown = resolved?.legal ? { target: resolved, world } : null;
    // The dragged end snaps to the resolved point, else rides the raw cursor.
    const tip = resolved ?? world;
    this.#host.wireLayer.setEndpointDrag({
      wireId: m.wireId,
      end: m.end,
      world: { x: tip.x * PX_PER_UNIT, y: tip.y * PX_PER_UNIT },
      legal: resolved ? resolved.legal : true,
    });
  };

  /**
   * Where the pointer at `world` would land `wireId`'s `end`: the NEAREST
   * connection point in snap reach that it may legally re-end at
   * (part-geometry.js's nearestLegalPoint, which every single-point drag
   * shares).
   * Returns `{ address, x, y, legal }`; `legal:false` (with whatever the raw
   * point resolves to) when nothing in range qualifies, so the illegal tint
   * still explains what's under the cursor; null when there's nothing there
   * at all. Asked by the preview alone; the drop commits what it answered.
   */
  #resolveEndpointTarget(wireId, end, world) {
    const doc = this.#host.doc;
    // One occupancy index for the gesture, not one per candidate per move.
    const m = this.#host.mode;
    const occupancy = m ? (m.occupancy ??= doc.occupancyIndex()) : null;
    const { point } = nearestLegalPoint(
      doc.boards,
      doc.components,
      world,
      (p) => doc.canReendWire(wireId, end, p.address, { occupancy }),
    );
    if (point) {
      return { address: point.address, x: point.x, y: point.y, legal: true };
    }
    const hit = this.#wirePointAt(world);
    return hit ? { ...hit, legal: false } : null;
  }

  #onEndpointUp = (e) => {
    const m = this.#host.mode;
    if (m?.kind !== "drag-wire-end" || e.pointerId !== m.pointerId) return;
    this.#host.mode = null;

    m.teardown?.();
    this.#host.ring.hidden = true;
    this.#host.ring.classList.remove("hole-ring--illegal");
    this.#host.viewport.classList.remove("desk-viewport--wire-dragging");
    this.#host.wireLayer.setEndpointDrag(null); // stop overriding; redraw

    if (!m.active) return; // plain click — the wire is already selected
    if (this.#aborted(e)) return; // aborted — never commit

    // The drop is the hole the ring was showing when the button came up —
    // NEVER re-resolved at the release point. A re-resolve there could pick a
    // different hole than the one circled, or the end's own origin (still in
    // reach on a short drag), and throw back a drop the screen had promised.
    // No ring, or a red one (m.shown is null for both), and the end reverts.
    if (!m.shown) return;
    const { target, world: at } = m.shown;
    if (target.address !== m.origin) {
      // An END dropped onto one of its own wire's bends absorbs it: the wire
      // now reaches where that waypoint was, so keeping it would leave a bend
      // sitting under the cap doing nothing. One undo step for both — the user
      // made one gesture.
      const merged = this.#pointNearOnWire(m.wireId, at);
      if (merged != null) this.#host.doc.removeWirePoint(m.wireId, merged);
      this.#host.doc.setWireEndpoint(m.wireId, m.end, target.address);
      this.#host.emitDocChanged("move wire"); // WireLayer re-renders from this
    }
  };

  /** The index of `wireId`'s own waypoint within merge range of `world`, or
      null — how an endpoint drop absorbs a bend it lands on. */
  #pointNearOnWire(wireId, world) {
    const hit = wirePointNear(
      [this.#host.doc.getWire(wireId)].filter(Boolean),
      world,
      WIRE_POINT_MERGE_RADIUS,
    );
    return hit ? hit.index : null;
  }

  #beginWholeDrag(wireId, e) {
    const wire = this.#host.doc.getWire(wireId);
    const from0 = this.#addressWorld(wire?.from);
    const to0 = this.#addressWorld(wire?.to);
    if (!from0 || !to0) return; // defensive: unresolvable endpoints
    this.#host.hideHover();
    this.#host.selectWire(wireId); // select on press (mode still null here)
    const mode = {
      kind: "drag-wire",
      wireId,
      from0, // origin endpoint world positions (pitch units)
      to0,
      fromOrigin: wire.from, // revert / no-op detection
      toOrigin: wire.to,
      shown: null, // the legal landing the last move showed (#onWholeMove)
      startWorld: this.#host.deskView.worldFromEvent(e),
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      active: false,
      teardown: null,
    };
    this.#host.mode = mode;
    mode.teardown = beginPointerGesture(
      this.#host.wireLayer.element,
      e.pointerId,
      { onMove: this.#onWholeMove, onEnd: this.#onWholeUp },
    );
  }

  #onWholeMove = (e) => {
    const m = this.#host.mode;
    if (m?.kind !== "drag-wire" || e.pointerId !== m.pointerId) return;
    if (!m.active) {
      const travel = Math.hypot(
        e.clientX - m.startClientX,
        e.clientY - m.startClientY,
      );
      if (travel < DRAG_THRESHOLD) return; // separate a click from a drag
      m.active = true;
      this.#host.viewport.classList.add("desk-viewport--wire-dragging");
    }
    const world = this.#host.deskView.worldFromEvent(e);
    m.lastWorld = world; // the fallback for a release with no position of its own
    // Rigid translation snapped to the 0.1-in lattice: ONE integer delta shifts
    // both ends together, so length and orientation never change. When the
    // raw delta doesn't land legally, #resolveWholeDragDelta looks for the
    // closest ADJUSTED delta that does — still rigid, never snapping just
    // one end independently of the other. What this finds is what the drop
    // commits (#onWholeUp) whenever it found anything.
    const { rawDx, rawDy } = this.#wholeDelta(m, world);
    const resolved = this.#resolveWholeDragDelta(m, rawDx, rawDy);
    // Snap the rendered ends onto the resolved holes when legal, else float
    // at the raw (unsnapped) delta so the drag still visibly tracks the cursor.
    const a = resolved?.fromHit ?? {
      x: m.from0.x + rawDx,
      y: m.from0.y + rawDy,
    };
    const b = resolved?.toHit ?? { x: m.to0.x + rawDx, y: m.to0.y + rawDy };
    // What the screen now SHOWS — the drop reuses it (#onWholeUp).
    m.shown = resolved;
    this.#host.wireLayer.setWholeDrag({
      wireId: m.wireId,
      from: { x: a.x * PX_PER_UNIT, y: a.y * PX_PER_UNIT },
      to: { x: b.x * PX_PER_UNIT, y: b.y * PX_PER_UNIT },
      legal: Boolean(resolved),
    });
  };

  /**
   * The delta a whole-wire drag starts its search from, for the pointer at
   * `world`: whole pitches across (the lattice), and DOWN the lattice of the
   * strip under the wire's `from` end — strips stack at fractional offsets
   * (a kit's pin-board rows on .01, a loose strip's on .51), so a rounded dy
   * could never carry a wire from one onto the other.
   */
  #wholeDelta(m, world) {
    const rawDx = Math.round(world.x - m.startWorld.x);
    const dy = world.y - m.startWorld.y;
    const hit = this.#wirePointAt({ x: m.from0.x + rawDx, y: m.from0.y + dy });
    const rawDy = hit ? Math.round((hit.y - m.from0.y) * 100) / 100 : Math.round(dy); // prettier-ignore
    return { rawDx, rawDy };
  }

  /**
   * The rigid delta a whole-wire drag would commit at, for a raw
   * (unsnapped) delta `(rawDx, rawDy)`: that exact delta when both
   * translated ends land legally, else the CLOSEST delta within
   * SNAP_RADIUS where they do — both ends always shift by the SAME
   * adjusted delta, so a near-miss recovers without ever breaking rigidity.
   * Null when nothing within range qualifies.
   */
  #resolveWholeDragDelta(m, rawDx, rawDy) {
    const doc = this.#host.doc;
    const occupancy = (m.occupancy ??= doc.occupancyIndex()); // one a gesture
    const at = (origin, dx, dy) =>
      this.#wirePointAt({ x: origin.x + dx, y: origin.y + dy });
    const found = nearestLegalOffset((ddx, ddy) => {
      const dx = rawDx + ddx;
      const dy = rawDy + ddy;
      const fromHit = at(m.from0, dx, dy);
      const toHit = at(m.to0, dx, dy);
      return Boolean(
        fromHit &&
        toHit &&
        doc.canMoveWire(m.wireId, fromHit.address, toHit.address, {
          occupancy,
        }),
      );
    }, SNAP_RADIUS);
    if (!found) return null;
    const dx = rawDx + found.dx;
    const dy = rawDy + found.dy;
    return { fromHit: at(m.from0, dx, dy), toHit: at(m.to0, dx, dy) };
  }

  #onWholeUp = (e) => {
    const m = this.#host.mode;
    if (m?.kind !== "drag-wire" || e.pointerId !== m.pointerId) return;
    this.#host.mode = null;
    m.teardown?.();
    this.#host.viewport.classList.remove("desk-viewport--wire-dragging");
    this.#host.wireLayer.setWholeDrag(null); // stop overriding; redraw from doc

    if (!m.active) return; // plain click — the wire is already selected
    if (this.#aborted(e)) return; // aborted — never commit

    // The drop is the landing the last move SHOWED, snapped and legal —
    // never re-resolved (CLAUDE.md → "Selection": a shown target is reused).
    // Only when nothing legal was showing does the release point get a say,
    // through the very same resolver (pointer-gesture.js's releaseWorld).
    let resolved = m.shown ?? null;
    if (!resolved) {
      const world = releaseWorld(this.#host.deskView, e, m.lastWorld);
      const { rawDx, rawDy } = this.#wholeDelta(m, world);
      resolved = this.#resolveWholeDragDelta(m, rawDx, rawDy);
    }
    // Commit only when BOTH ends landed on real free points (a legal, moved
    // target); an invalid release cancels the drag-drop and the wire snaps back.
    const from = resolved?.fromHit.address;
    const to = resolved?.toHit.address;
    if (resolved && (from !== m.fromOrigin || to !== m.toOrigin)) {
      this.#host.doc.moveWire(m.wireId, from, to);
      this.#host.emitDocChanged("move wire"); // WireLayer re-renders from this
    }
  };
}
